import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  LOYALTY_PROGRAM_QUERY_KEY,
  deleteLoyaltyProgramOverride,
  fetchLoyaltyProgramConfig,
  type LoyaltyEarnMode,
  type LoyaltyProgramOverrideRecord,
  type LoyaltyProgramRecord,
  type LoyaltyProgramStatus,
  type LoyaltyRoundingMode,
  type LoyaltyProgramUpdateInput,
  updateLoyaltyProgram,
  upsertLoyaltyProgramOverride,
} from "@/lib/api/loyalty";
import { fetchTenantLocations, type TenantLocationRecord } from "@/lib/api/settings";

type LoyaltyProgramManagerProps = {
  onClose?: () => void;
};

type ProgramDraft = {
  status: LoyaltyProgramStatus;
  earnMode: LoyaltyEarnMode;
  pointsPerCurrency: string;
  currencyUnit: string;
  pointsPerOrder: string;
  roundingMode: LoyaltyRoundingMode;
  notes: string;
  redemptionEnabled: 'enabled' | 'disabled';
  redemptionPointsPerUnit: string;
  redemptionCurrencyUnit: string;
  redemptionMinPoints: string;
  redemptionMaxPercent: string;
};

type OverrideDialogState =
  | {
      type: "create";
      draft: ProgramDraft;
      locationId: string | null;
    }
  | {
      type: "edit";
      draft: ProgramDraft;
      locationId: string;
    }
  | null;

const STATUS_BADGE_CLASS: Record<LoyaltyProgramStatus, string> = {
  enabled: "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:bg-emerald-400/15 dark:text-emerald-100",
  disabled: "bg-slate-500/10 text-slate-600 ring-slate-500/15 dark:bg-slate-400/15 dark:text-slate-100",
  preview: "bg-amber-500/10 text-amber-600 ring-amber-500/20 dark:bg-amber-400/15 dark:text-amber-100",
};

const EARN_MODE_LABEL: Record<LoyaltyEarnMode, string> = {
  per_spend: "Per spend",
  per_order: "Per order",
  hybrid: "Hybrid",
};

const ROUNDING_LABEL: Record<LoyaltyRoundingMode, string> = {
  floor: "Round down",
  ceil: "Round up",
  nearest: "Nearest whole number",
};

const createProgramDraft = (record: LoyaltyProgramRecord): ProgramDraft => ({
  status: record.status,
  earnMode: record.earnMode,
  pointsPerCurrency: record.pointsPerCurrency.toString(),
  currencyUnit: record.currencyUnit.toString(),
  pointsPerOrder: record.pointsPerOrder != null ? record.pointsPerOrder.toString() : "",
  roundingMode: record.roundingMode,
  notes: record.notes ?? "",
  redemptionEnabled: record.redemptionEnabled ? "enabled" : "disabled",
  redemptionPointsPerUnit: record.redemptionPointsPerUnit?.toString() ?? "0",
  redemptionCurrencyUnit: record.redemptionCurrencyUnit?.toString() ?? "100",
  redemptionMinPoints: record.redemptionMinPoints?.toString() ?? "0",
  redemptionMaxPercent: record.redemptionMaxPercent?.toString() ?? "100",
});

const createOverrideDraft = (
  override: LoyaltyProgramOverrideRecord | null,
  fallback: LoyaltyProgramRecord,
): ProgramDraft => {
  if (!override) {
    return createProgramDraft(fallback);
  }

  return {
    status: override.status ?? fallback.status,
    earnMode: override.earnMode ?? fallback.earnMode,
    pointsPerCurrency: (override.pointsPerCurrency ?? fallback.pointsPerCurrency).toString(),
    currencyUnit: (override.currencyUnit ?? fallback.currencyUnit).toString(),
    pointsPerOrder:
      override.pointsPerOrder != null
        ? override.pointsPerOrder.toString()
        : fallback.pointsPerOrder != null
          ? fallback.pointsPerOrder.toString()
          : "",
    roundingMode: override.roundingMode ?? fallback.roundingMode,
    notes: override.notes ?? fallback.notes ?? "",
    redemptionEnabled: (override.redemptionEnabled ?? fallback.redemptionEnabled) ? "enabled" : "disabled",
    redemptionPointsPerUnit: (
      override.redemptionPointsPerUnit ?? fallback.redemptionPointsPerUnit ?? 0
    ).toString(),
    redemptionCurrencyUnit: (
      override.redemptionCurrencyUnit ?? fallback.redemptionCurrencyUnit ?? 100
    ).toString(),
    redemptionMinPoints: (
      override.redemptionMinPoints ?? fallback.redemptionMinPoints ?? 0
    ).toString(),
    redemptionMaxPercent: (
      override.redemptionMaxPercent ?? fallback.redemptionMaxPercent ?? 100
    ).toString(),
  };
};

const buildPayloadFromDraft = (draft: ProgramDraft): LoyaltyProgramUpdateInput => {
  const pointsPerCurrency = Number.parseFloat(draft.pointsPerCurrency);
  const currencyUnit = Number.parseInt(draft.currencyUnit, 10);
  const pointsPerOrder =
    draft.pointsPerOrder.trim().length > 0 ? Number.parseFloat(draft.pointsPerOrder) : null;
  const redemptionPointsPerUnit =
    draft.redemptionPointsPerUnit.trim().length > 0
      ? Number.parseFloat(draft.redemptionPointsPerUnit)
      : null;
  const redemptionCurrencyUnit =
    draft.redemptionCurrencyUnit.trim().length > 0
      ? Number.parseInt(draft.redemptionCurrencyUnit, 10)
      : null;
  const redemptionMinPoints =
    draft.redemptionMinPoints.trim().length > 0
      ? Number.parseInt(draft.redemptionMinPoints, 10)
      : null;
  const redemptionMaxPercent =
    draft.redemptionMaxPercent.trim().length > 0
      ? Number.parseInt(draft.redemptionMaxPercent, 10)
      : null;

  return {
    status: draft.status,
    earnMode: draft.earnMode,
    pointsPerCurrency: Number.isFinite(pointsPerCurrency) ? pointsPerCurrency : 0,
    currencyUnit: Number.isFinite(currencyUnit) && currencyUnit > 0 ? currencyUnit : 100,
    pointsPerOrder: pointsPerOrder !== null && Number.isFinite(pointsPerOrder) ? pointsPerOrder : null,
    roundingMode: draft.roundingMode,
    notes: draft.notes.trim().length > 0 ? draft.notes.trim() : null,
    redemptionEnabled: draft.redemptionEnabled === "enabled",
    redemptionPointsPerUnit:
      redemptionPointsPerUnit !== null && Number.isFinite(redemptionPointsPerUnit)
        ? redemptionPointsPerUnit
        : null,
    redemptionCurrencyUnit:
      redemptionCurrencyUnit !== null && Number.isFinite(redemptionCurrencyUnit)
        ? redemptionCurrencyUnit
        : null,
    redemptionMinPoints:
      redemptionMinPoints !== null && Number.isFinite(redemptionMinPoints) ? redemptionMinPoints : null,
    redemptionMaxPercent:
      redemptionMaxPercent !== null && Number.isFinite(redemptionMaxPercent) ? redemptionMaxPercent : null,
  };
};

const deriveProgramHash = (draft: ProgramDraft): string =>
  JSON.stringify([
    draft.status,
    draft.earnMode,
    draft.pointsPerCurrency,
    draft.currencyUnit,
    draft.pointsPerOrder,
    draft.roundingMode,
    draft.notes,
    draft.redemptionEnabled,
    draft.redemptionPointsPerUnit,
    draft.redemptionCurrencyUnit,
    draft.redemptionMinPoints,
    draft.redemptionMaxPercent,
  ]);

const formatSpendBlockAmount = (unit: number, locale: string): string => {
  if (!Number.isFinite(unit) || unit <= 0) {
    return "-";
  }
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(unit / 100);
  } catch {
    return (unit / 100).toFixed(2);
  }
};

export function LoyaltyProgramManager({ onClose }: LoyaltyProgramManagerProps): JSX.Element {
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation(["settings"]);
  const resolvedLocale =
    i18n.language && i18n.language.length > 0
      ? i18n.language
      : typeof navigator !== "undefined"
        ? navigator.language
        : "en-US";
  const [programDraft, setProgramDraft] = useState<ProgramDraft | null>(null);
  const [baselineHash, setBaselineHash] = useState<string>("");
  const [overrideDialogState, setOverrideDialogState] = useState<OverrideDialogState>(null);

  const programQuery = useQuery({
    queryKey: LOYALTY_PROGRAM_QUERY_KEY,
    queryFn: ({ signal }) => fetchLoyaltyProgramConfig(signal),
    staleTime: 60_000,
  });

  const locationsQuery = useQuery({
    queryKey: ['settings', 'locations'],
    queryFn: ({ signal }) => fetchTenantLocations(signal),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!programQuery.data) {
      return;
    }

    const nextDraft = createProgramDraft(programQuery.data.program);
    setProgramDraft(nextDraft);
    setBaselineHash(deriveProgramHash(nextDraft));
  }, [programQuery.data]);

  const updateProgramMutation = useMutation({
    mutationFn: (payload: LoyaltyProgramUpdateInput) => updateLoyaltyProgram(payload),
    onSuccess: (program) => {
      const nextDraft = createProgramDraft(program);
      setProgramDraft(nextDraft);
      setBaselineHash(deriveProgramHash(nextDraft));
      toast.success("Loyalty program updated");
      queryClient.invalidateQueries({ queryKey: LOYALTY_PROGRAM_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unable to update loyalty program right now.";
      toast.error("Failed to update loyalty program", { description: message });
    },
  });

  const overrideMutation = useMutation({
    mutationFn: async ({ locationId, payload }: { locationId: string; payload: LoyaltyProgramUpdateInput }) =>
      upsertLoyaltyProgramOverride(locationId, payload),
    onSuccess: () => {
      toast.success("Override saved");
      setOverrideDialogState(null);
      queryClient.invalidateQueries({ queryKey: LOYALTY_PROGRAM_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unable to save override right now.";
      toast.error("Failed to save override", { description: message });
    },
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (locationId: string) => deleteLoyaltyProgramOverride(locationId),
    onSuccess: () => {
      toast.success("Override removed");
      queryClient.invalidateQueries({ queryKey: LOYALTY_PROGRAM_QUERY_KEY });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "Unable to remove override right now.";
      toast.error("Failed to remove override", { description: message });
    },
  });

  const locations = useMemo(() => locationsQuery.data?.locations ?? [], [locationsQuery.data]);
  const locationMap = useMemo(
    () =>
      new Map<string, TenantLocationRecord>(
        locations.map((location) => [location.id, location]),
      ),
    [locations],
  );

  const overrides = useMemo(() => programQuery.data?.overrides ?? [], [programQuery.data]);
  const effectiveProgram = programQuery.data?.effective ?? null;
  const programRecord = programQuery.data?.program ?? null;

  const programDirty =
    programDraft !== null && baselineHash.length > 0 && deriveProgramHash(programDraft) !== baselineHash;

  const availableOverrideLocations = useMemo(() => {
    if (!locations.length) {
      return [];
    }
    const used = new Set(overrides.map((override) => override.locationId));
    return locations.filter((location) => !used.has(location.id));
  }, [locations, overrides]);

  const handleProgramFieldChange = <K extends keyof ProgramDraft>(key: K, value: ProgramDraft[K]) => {
    setProgramDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
  };

  const handleOverrideFieldChange = <K extends keyof ProgramDraft>(
    key: K,
    value: ProgramDraft[K],
  ) => {
    setOverrideDialogState((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [key]: value,
            },
          }
        : current,
    );
  };

  const handleResetProgram = () => {
    if (!programRecord) {
      return;
    }
    const nextDraft = createProgramDraft(programRecord);
    setProgramDraft(nextDraft);
    setBaselineHash(deriveProgramHash(nextDraft));
  };

  const handleSaveProgram = () => {
    if (!programDraft) {
      return;
    }
    updateProgramMutation.mutate(buildPayloadFromDraft(programDraft));
  };

  const openCreateOverride = () => {
    if (!programRecord) {
      return;
    }
    setOverrideDialogState({
      type: "create",
      draft: createProgramDraft(programRecord),
      locationId: availableOverrideLocations[0]?.id ?? null,
    });
  };

  const openEditOverride = (override: LoyaltyProgramOverrideRecord) => {
    if (!programRecord) {
      return;
    }
    setOverrideDialogState({
      type: "edit",
      draft: createOverrideDraft(override, programRecord),
      locationId: override.locationId,
    });
  };

  const handleSubmitOverride = () => {
    if (!overrideDialogState) {
      return;
    }
    if (overrideDialogState.type === "create" && !overrideDialogState.locationId) {
      toast.error("Select a location for the override.");
      return;
    }
    const locationId = overrideDialogState.locationId!;
    const payload = buildPayloadFromDraft(overrideDialogState.draft);
    overrideMutation.mutate({ locationId, payload });
  };

  const handleDeleteOverride = (override: LoyaltyProgramOverrideRecord) => {
    const location = locationMap.get(override.locationId);
    const name = location?.name ?? override.locationId;
    if (
      window.confirm(`Remove the loyalty override for ${name}? This location will fall back to the tenant default.`)
    ) {
      deleteOverrideMutation.mutate(override.locationId);
    }
  };

  const inboundIsLoading = programQuery.isLoading || locationsQuery.isLoading;

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle>Loyalty program</CardTitle>
          <CardDescription>Control how guests earn points across the tenant.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {inboundIsLoading && !programDraft ? (
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-16 rounded-xl" />
              ))}
            </div>
          ) : programDraft ? (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={programDraft.status} onValueChange={(value) => handleProgramFieldChange("status", value as LoyaltyProgramStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enabled">Enabled</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="preview">Preview</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Earn mode</Label>
                  <Select
                    value={programDraft.earnMode}
                    onValueChange={(value) => handleProgramFieldChange("earnMode", value as LoyaltyEarnMode)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_spend">Per spend</SelectItem>
                      <SelectItem value="per_order">Per order</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.pointsPerSpend.label", {
                      defaultValue: "Points earned per spend block",
                    })}
                  </Label>
                  <Input
                    inputMode="decimal"
                    value={programDraft.pointsPerCurrency}
                    onChange={(event) => handleProgramFieldChange("pointsPerCurrency", event.target.value)}
                    placeholder="1.0"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.loyalty.fields.pointsPerSpend.help", {
                      defaultValue:
                        "Guests earn this many points every time the spend block below is reached. Decimals are welcome.",
                    })}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.currencyUnit.label", {
                      defaultValue: "Spend block amount (in cents)",
                    })}
                  </Label>
                  <Input
                    inputMode="numeric"
                    value={programDraft.currencyUnit}
                    onChange={(event) => handleProgramFieldChange("currencyUnit", event.target.value)}
                    placeholder="100"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.loyalty.fields.currencyUnit.help", {
                      defaultValue:
                        "Most teams keep this at 100 (=$1.00). Increase it if you want points to accrue for larger spend blocks.",
                    })}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.pointsPerOrder.label", {
                      defaultValue: "Bonus points per order (optional)",
                    })}
                  </Label>
                  <Input
                    inputMode="decimal"
                    value={programDraft.pointsPerOrder}
                    onChange={(event) => handleProgramFieldChange("pointsPerOrder", event.target.value)}
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.loyalty.fields.pointsPerOrder.help", {
                      defaultValue: "Guests receive this bonus on every qualifying order. Leave blank to skip the bonus.",
                    })}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Rounding</Label>
                  <Select
                    value={programDraft.roundingMode}
                    onValueChange={(value) => handleProgramFieldChange("roundingMode", value as LoyaltyRoundingMode)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="floor">Round down</SelectItem>
                      <SelectItem value="nearest">Nearest whole number</SelectItem>
                      <SelectItem value="ceil">Round up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    value={programDraft.notes}
                    onChange={(event) => handleProgramFieldChange("notes", event.target.value)}
                    placeholder="Visible to staff configuring overrides."
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-foreground">Redemption rules</p>
                  <p className="text-xs text-muted-foreground">
                    Decide how guests can spend their balance once they earn points.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Allow redemptions</Label>
                    <Select
                      value={programDraft.redemptionEnabled}
                      onValueChange={(value) =>
                        handleProgramFieldChange("redemptionEnabled", value as 'enabled' | 'disabled')
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enabled">Enabled</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Turn this off if you only want to accrue points without letting guests spend them yet.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum points per redemption</Label>
                    <Input
                      inputMode="numeric"
                      value={programDraft.redemptionMinPoints}
                      onChange={(event) => handleProgramFieldChange("redemptionMinPoints", event.target.value)}
                      placeholder="0"
                    />
                    <p className="text-xs text-muted-foreground">
                      Blocks redemptions until guests collect at least this many points.
                    </p>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Conversion</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        inputMode="decimal"
                        value={programDraft.redemptionPointsPerUnit}
                        onChange={(event) =>
                          handleProgramFieldChange("redemptionPointsPerUnit", event.target.value)
                        }
                        className="w-28"
                        placeholder="100"
                      />
                      <span className="text-xs text-muted-foreground">points unlock</span>
                      <Input
                        inputMode="numeric"
                        value={programDraft.redemptionCurrencyUnit}
                        onChange={(event) =>
                          handleProgramFieldChange("redemptionCurrencyUnit", event.target.value)
                        }
                        className="w-28"
                        placeholder="100"
                      />
                      <span className="text-xs text-muted-foreground">cents of credit</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      For example, 100 points unlocking 100 cents = $1.00 per 100 points.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Max % of subtotal covered</Label>
                    <Input
                      inputMode="numeric"
                      value={programDraft.redemptionMaxPercent}
                      onChange={(event) => handleProgramFieldChange("redemptionMaxPercent", event.target.value)}
                      placeholder="50"
                    />
                    <p className="text-xs text-muted-foreground">
                      Prevents redemptions from covering more than this percentage of a check.
                    </p>
                  </div>
                </div>
              </div>
              {programRecord ? (
                <div className="rounded-xl border border-border/70 bg-muted/30 px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">Effective status</p>
                      <p className="text-xs text-muted-foreground">
                        Updated{" "}
                        {formatDistanceToNow(new Date(programRecord.updatedAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                    <Badge className={STATUS_BADGE_CLASS[effectiveProgram?.status ?? programRecord.status]}>
                      {(effectiveProgram?.status ?? programRecord.status).toUpperCase()}
                    </Badge>
                  </div>
                  <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                    <div>
                      <span className="block font-semibold text-foreground">Earn mode</span>
                      <span>{EARN_MODE_LABEL[effectiveProgram?.earnMode ?? programRecord.earnMode]}</span>
                    </div>
                    <div>
                      <span className="block font-semibold text-foreground">
                        {t("settings.loyalty.summary.pointsPerSpend.title", {
                          defaultValue: "Points per spend block",
                        })}
                      </span>
                      <span>
                        {t("settings.loyalty.summary.pointsPerSpend.value", {
                          defaultValue: "{{points}} pts for every {{amount}} in sales",
                          points: (effectiveProgram?.pointsPerCurrency ?? programRecord.pointsPerCurrency).toFixed(2),
                          amount: formatSpendBlockAmount(
                            effectiveProgram?.currencyUnit ?? programRecord.currencyUnit,
                            resolvedLocale,
                          ),
                        })}
                      </span>
                    </div>
                    <div>
                      <span className="block font-semibold text-foreground">Rounding</span>
                      <span>{ROUNDING_LABEL[effectiveProgram?.roundingMode ?? programRecord.roundingMode]}</span>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="flex flex-wrap items-center justify-end gap-3">
                <Button variant="outline" onClick={handleResetProgram} disabled={!programDirty || updateProgramMutation.isLoading}>
                  Reset
                </Button>
                <Button onClick={handleSaveProgram} disabled={!programDirty || updateProgramMutation.isLoading}>
                  {updateProgramMutation.isLoading ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Unable to load the loyalty program right now.</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-1">
          <CardTitle>Location overrides</CardTitle>
          <CardDescription>
            Override earn rules for specific locations while keeping a tenant-wide default.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Overrides fall back to the tenant default when removed.
            </div>
            <Button
              onClick={openCreateOverride}
              variant="outline"
              disabled={availableOverrideLocations.length === 0 || programRecord === null}
            >
              Add override
            </Button>
          </div>
          <Separator />
          {overrides.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/60 p-6 text-sm text-muted-foreground">
              No location overrides yet. Add one to adjust earn rules for a specific venue.
            </div>
          ) : (
            <div className="space-y-4">
              {overrides.map((override) => {
                const location = locationMap.get(override.locationId);
                const effectiveStatus = override.status ?? programRecord?.status ?? 'disabled';
                return (
                  <div
                    key={override.id}
                    className="rounded-xl border border-border/60 bg-muted/20 p-4 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {location?.name ?? override.locationId}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Updated {formatDistanceToNow(new Date(override.updatedAt), { addSuffix: true })}
                        </p>
                      </div>
                      <Badge className={STATUS_BADGE_CLASS[effectiveStatus]}>
                        {effectiveStatus.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-muted-foreground md:grid-cols-3">
                      <div>
                        <span className="block font-semibold text-foreground">Earn mode</span>
                        <span>{EARN_MODE_LABEL[override.earnMode ?? programRecord?.earnMode ?? 'per_spend']}</span>
                      </div>
                      <div>
                        <span className="block font-semibold text-foreground">
                          {t("settings.loyalty.summary.pointsPerSpend.title", {
                            defaultValue: "Points per spend block",
                          })}
                        </span>
                        <span>
                          {t("settings.loyalty.summary.pointsPerSpend.value", {
                            defaultValue: "{{points}} pts for every {{amount}} in sales",
                            points: (override.pointsPerCurrency ?? programRecord?.pointsPerCurrency ?? 0).toFixed(2),
                            amount: formatSpendBlockAmount(
                              override.currencyUnit ?? programRecord?.currencyUnit ?? 100,
                              resolvedLocale,
                            ),
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="block font-semibold text-foreground">
                          {t("settings.loyalty.summary.pointsPerOrder.title", {
                            defaultValue: "Bonus points per order",
                          })}
                        </span>
                        <span>
                          {override.pointsPerOrder ?? programRecord?.pointsPerOrder ?? 0}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditOverride(override)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive"
                        onClick={() => handleDeleteOverride(override)}
                        disabled={deleteOverrideMutation.isLoading}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={overrideDialogState !== null} onOpenChange={(open) => !open && setOverrideDialogState(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {overrideDialogState?.type === "edit" ? "Edit location override" : "Add location override"}
            </DialogTitle>
            <DialogDescription>
              Configure earn rules for a specific location. Removing the override will fall back to the tenant default.
            </DialogDescription>
          </DialogHeader>
          {overrideDialogState && programRecord ? (
            <div className="space-y-4">
              {overrideDialogState.type === "create" ? (
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={overrideDialogState.locationId ?? undefined}
                    onValueChange={(value) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              locationId: value,
                            }
                          : current,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOverrideLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input value={locationMap.get(overrideDialogState.locationId)?.name ?? overrideDialogState.locationId} disabled />
                </div>
              )}
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={overrideDialogState.draft.status}
                    onValueChange={(value) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, status: value as LoyaltyProgramStatus },
                            }
                          : current,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enabled">Enabled</SelectItem>
                      <SelectItem value="disabled">Disabled</SelectItem>
                      <SelectItem value="preview">Preview</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Earn mode</Label>
                  <Select
                    value={overrideDialogState.draft.earnMode}
                    onValueChange={(value) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, earnMode: value as LoyaltyEarnMode },
                            }
                          : current,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="per_spend">Per spend</SelectItem>
                      <SelectItem value="per_order">Per order</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.pointsPerSpend.label", {
                      defaultValue: "Points earned per spend block",
                    })}
                  </Label>
                  <Input
                    inputMode="decimal"
                    value={overrideDialogState.draft.pointsPerCurrency}
                    onChange={(event) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, pointsPerCurrency: event.target.value },
                            }
                          : current,
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.currencyUnit.label", {
                      defaultValue: "Spend block amount (in cents)",
                    })}
                  </Label>
                  <Input
                    inputMode="numeric"
                    value={overrideDialogState.draft.currencyUnit}
                    onChange={(event) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, currencyUnit: event.target.value },
                            }
                          : current,
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    {t("settings.loyalty.fields.pointsPerOrder.label", {
                      defaultValue: "Bonus points per order (optional)",
                    })}
                  </Label>
                  <Input
                    inputMode="decimal"
                    value={overrideDialogState.draft.pointsPerOrder}
                    onChange={(event) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, pointsPerOrder: event.target.value },
                            }
                          : current,
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rounding</Label>
                  <Select
                    value={overrideDialogState.draft.roundingMode}
                    onValueChange={(value) =>
                      setOverrideDialogState((current) =>
                        current
                          ? {
                              ...current,
                              draft: { ...current.draft, roundingMode: value as LoyaltyRoundingMode },
                            }
                          : current,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="floor">Round down</SelectItem>
                      <SelectItem value="nearest">Nearest whole number</SelectItem>
                      <SelectItem value="ceil">Round up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  value={overrideDialogState.draft.notes}
                  onChange={(event) =>
                    setOverrideDialogState((current) =>
                      current
                        ? {
                            ...current,
                            draft: { ...current.draft, notes: event.target.value },
                          }
                        : current,
                    )
                  }
                  placeholder="Visible to staff configuring overrides."
                />
              </div>
              <Separator />
              <div className="space-y-4">
                <p className="text-sm font-semibold text-foreground">Redemption rules</p>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Allow redemptions</Label>
                    <Select
                      value={overrideDialogState.draft.redemptionEnabled}
                      onValueChange={(value) =>
                        handleOverrideFieldChange("redemptionEnabled", value as 'enabled' | 'disabled')
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="enabled">Enabled</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Minimum points</Label>
                    <Input
                      inputMode="numeric"
                      value={overrideDialogState.draft.redemptionMinPoints}
                      onChange={(event) => handleOverrideFieldChange("redemptionMinPoints", event.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Conversion</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        inputMode="decimal"
                        value={overrideDialogState.draft.redemptionPointsPerUnit}
                        onChange={(event) =>
                          handleOverrideFieldChange("redemptionPointsPerUnit", event.target.value)
                        }
                        className="w-28"
                        placeholder="100"
                      />
                      <span className="text-xs text-muted-foreground">points =</span>
                      <Input
                        inputMode="numeric"
                        value={overrideDialogState.draft.redemptionCurrencyUnit}
                        onChange={(event) =>
                          handleOverrideFieldChange("redemptionCurrencyUnit", event.target.value)
                        }
                        className="w-28"
                        placeholder="100"
                      />
                      <span className="text-xs text-muted-foreground">cents</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Max % of subtotal</Label>
                    <Input
                      inputMode="numeric"
                      value={overrideDialogState.draft.redemptionMaxPercent}
                      onChange={(event) => handleOverrideFieldChange("redemptionMaxPercent", event.target.value)}
                      placeholder="50"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogState(null)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitOverride} disabled={overrideMutation.isLoading}>
              {overrideMutation.isLoading ? "Saving..." : "Save override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {onClose ? (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : null}
    </div>
  );
}
